// Reminder mail: default templates, variables, and preview values.
//
// emailTemplates.js의 짝이고, 거기서 다르지 않은 것은 전부 가져다 쓴다 — 렌더 규칙,
// 제목 길이, 자산 URL 정규화는 두 메일이 공유하는 물건이라 여기서 다시 정의하지 않는다.
//
// 다른 것은 변수 목록 하나다. {{password}}가 없다. 알림은 비밀번호를 재발급하지 않고
// (그것이 알림과 Resend를 가르는 지점이다) 서버는 사이트가 들고 있는 비밀번호의 scrypt
// 해시만 갖고 있으므로, 본문에 {{password}}를 써도 치환될 평문이 존재하지 않는다.
// 그대로 두면 사이트에 리터럴 "{{password}}"가 도착하므로 저장과 발송 양쪽에서 막는다.
import {
  SUBJECT_MAX, REQUIRED_VAR, hasRequiredVars,
  renderPreviewHtml, renderPreviewText, normalizeAssetUrls,
  effectiveExpiry,
} from "./emailTemplates";

export { SUBJECT_MAX, REQUIRED_VAR, hasRequiredVars, renderPreviewHtml, renderPreviewText,
         normalizeAssetUrls };

// 초대 변수에서 password를 빼고 sentDate/daysSince를 더한 것. 뒤의 둘은 알림에만 있고,
// 재촉하는 메일을 재촉처럼 읽히게 만드는 값이다 — "언제 보냈던 것"인지 없이 링크만 다시
// 보내면 사이트 입장에서는 같은 메일이 두 번 온 것과 구분되지 않는다.
// API의 REMINDER_TEMPLATE_VARS(services/Feasibility/email.js)와 같이 움직여야 한다.
export const REMINDER_TEMPLATE_VARS = [
  { key: "piName",    label: "PI 이름" },
  { key: "siteName",  label: "Site 명" },
  { key: "publicUrl", label: "설문 링크", required: true },
  { key: "expiry",    label: "만료일" },
  { key: "fromName",  label: "보내는 사람" },
  { key: "sentDate",  label: "초대 발송일" },
  { key: "daysSince", label: "경과 일수" },
];

// 저장/발송을 막는 가드. API(reminder.js의 hasPasswordVar)와 같은 판정이다.
export const hasPasswordVar = (s) => String(s || "").includes("{{password}}");

export const DEFAULT_REMINDER_TEMPLATES = {
  ko: {
    subject: "[알림] 임상시험 실시기관 사전 타당성 조사 (SFQ) 회신 요청 - {{siteName}}",
    bodyHtml: `<p>{{piName}} 선생님께,</p>
<p><b>{{sentDate}}</b>에 <b>{{siteName}}</b>으로 발송해 드린 임상시험 실시기관 사전 타당성 조사(SFQ)가
아직 제출되지 않아 안내드립니다. (발송 후 {{daysSince}}일 경과)</p>
<p>아래 링크를 통해 설문에 접속하실 수 있으며, 링크는 <b>{{expiry}}</b>까지 유효합니다.</p>
<p><a href="{{publicUrl}}">{{publicUrl}}</a></p>
<p><b>접속 비밀번호는 처음 보내드린 초대 메일의 비밀번호를 그대로 사용하시면 됩니다.</b>
이 메일은 새 비밀번호를 발급하지 않으며, 이미 작성 중이신 답변은 그대로 보존되어 있습니다.
비밀번호를 확인하기 어려우시면 담당 CRA에게 재발송을 요청해 주시기 바랍니다.</p>
<p>이미 제출을 완료하셨다면 본 메일은 무시하셔도 됩니다.</p>
<br><p>감사합니다.<br>{{fromName}} 드림</p>
<hr><p style="font-size:11px;color:#888;">본 메일은 기밀 정보를 포함하고 있습니다. 잘못 수신하신 경우 삭제해 주시기 바랍니다.</p>`,
  },
  en: {
    subject: "[Reminder] Site Feasibility Questionnaire - {{siteName}}",
    bodyHtml: `<p>Dear {{piName}},</p>
<p>We are following up on the Site Feasibility Questionnaire (SFQ) sent to <b>{{siteName}}</b>
on <b>{{sentDate}}</b> ({{daysSince}} days ago), which has not been submitted yet.</p>
<p>Please access the questionnaire via the link below. The link is valid until <b>{{expiry}}</b>.</p>
<p><a href="{{publicUrl}}">{{publicUrl}}</a></p>
<p><b>Please use the access password from the original invitation email.</b>
This reminder does not issue a new password, and any answers you have already saved are kept.
If you no longer have the password, please contact your assigned CRA to request a resend.</p>
<p>If you have already submitted, please disregard this message.</p>
<br><p>Best regards,<br>{{fromName}}</p>
<hr><p style="font-size:11px;color:#888;">This message is confidential. If received in error, please delete it.</p>`,
  },
};

// 발송될 값들. 알림은 만료일을 갱신하지 않으므로 effectiveExpiry가 아니라 행에 적힌
// 만료일을 그대로 쓴다 — 갱신되는 날짜를 미리 보여주는 초대 작성기와 다른 점이다.
// (만료된 링크에는 애초에 알림을 보내지 않는다.)
export function buildReminderVars({ inv, publicUrl, fromName, lang }) {
  const locale = lang === "ko" ? "ko-KR" : "en-US";
  const opts = { year: "numeric", month: "long", day: "numeric" };

  const expiryDate = inv?.siteTokenExpiry ? new Date(inv.siteTokenExpiry) : effectiveExpiry(inv);
  const sent = inv?.sentAt ? new Date(inv.sentAt) : null;

  return {
    piName:   inv?.piName   || (lang === "ko" ? "PI 담당자" : "PI"),
    siteName: inv?.siteName || inv?.siteCode || (lang === "ko" ? "귀 기관" : "your site"),
    publicUrl: publicUrl || "",
    expiry:   expiryDate.toLocaleDateString(locale, opts),
    fromName: fromName || "CRO Clinical Team",
    sentDate: sent ? sent.toLocaleDateString(locale, opts) : "-",
    daysSince: sent
      ? String(Math.max(0, Math.floor((Date.now() - sent.getTime()) / 86400000)))
      : "-",
  };
}

// 템플릿 편집기용 표본값 — 편집기에는 대상 사이트가 없다.
export const REMINDER_SAMPLE_VARS = {
  ko: {
    piName: "홍길동", siteName: "서울대학교병원",
    publicUrl: "https://example.com/sfq-site/xxxx", expiry: "2026년 8월 13일",
    fromName: "김담당", sentDate: "2026년 7월 14일", daysSince: "7",
  },
  en: {
    piName: "Dr. Hong", siteName: "Seoul National University Hospital",
    publicUrl: "https://example.com/sfq-site/xxxx", expiry: "August 13, 2026",
    fromName: "Kim CRA", sentDate: "July 14, 2026", daysSince: "7",
  },
};

// ── 목록에 보여줄 알림 상태 ───────────────────────────────────────────────────
//
// 한 행의 "알림" 칼럼이 답해야 하는 질문은 "이 사이트에 자동 알림이 나가는가, 그렇다면
// 얼마나 더 나가는가"다. API의 listDueReminders가 행을 고르는 규칙과 같은 판정이라
// 둘을 같이 고쳐야 한다.
//
// 여기서 "대상 아님"과 "꺼짐"을 구분하는 이유: 전자는 상태가 바뀌면 저절로 대상이 되지만
// (Pending 사이트에 초대를 보내면 그때부터 알림이 붙는다), 후자는 누군가 끈 것이라
// 켜기 전에는 영영 나가지 않는다.
export const REMINDER_ELIGIBLE_STATUS = ["Sent", "InProgress"];

export function reminderState(inv, settings) {
  if (!inv || !REMINDER_ELIGIBLE_STATUS.includes(inv.status)) return { kind: "n/a" };

  const linkOpen = !inv.revokedAt
    && inv.siteTokenExpiry && new Date(inv.siteTokenExpiry) > new Date();
  if (!linkOpen) return { kind: "linkClosed" };

  if (inv.reminderOptOut) return { kind: "off", reason: "site" };
  if (!settings?.enabled) return { kind: "off", reason: "study" };

  const days = inv.reminderIntervalDays || settings.intervalDays;
  const sentCount = inv.reminderSentCount || 0;
  const max = settings.maxCount;

  if (sentCount >= max) return { kind: "done", sentCount, max };
  return { kind: "on", days, sentCount, max, custom: !!inv.reminderIntervalDays };
}
